import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { createSelector } from 'reselect'
import type { State } from 'types/redux'
import * as FEATURES from '@disco/constants/FeatureTypes'
import { getUser } from '@disco/state-management/selectors/user'
import type { TrialLeft, TrialMessageMode } from '@disco/utils/trial'
import { getTrialLeft } from '@disco/utils/trial'
import type { Plan } from 'types/business'
const exclusionPairs = {
  [FEATURES.PLAYLIST_THEMES_LIMITED]: FEATURES.PLAYLIST_THEMES_UNLIMITED,
  [FEATURES.CHANNELS_LIMITED]: FEATURES.CHANNELS_UNLIMITED,
  [FEATURES.ANALYTICS_LIMITED]: FEATURES.ANALYTICS_UNLIMITED,
}
// TO DO: this map and  is deprecated and possibly stale.
// It is used to generate labels for getBadgeTitle.
// We need to either re-consider labels on mockups or to change back-end data structure.
const LITE = 'lite'
const PLUS = 'plus'
export const PRO = 'pro'
const ENTERPRISE = 'enterprise'
const featureLabelMap = {
  [FEATURES.PLAYLIST_THEMES_LIMITED]: LITE,
  [FEATURES.ADMIN_PERMISSION_MANAGEMENT_MEDIUM_LEVEL]: PLUS,
  [FEATURES.ADVANCED_SEARCH]: PLUS,
  [FEATURES.ANALYTICS_LIMITED]: PLUS,
  [FEATURES.ASSIGN_URL_TO_CONTACTS]: PLUS,
  [FEATURES.CHANNELS_UNLIMITED]: PLUS,
  [FEATURES.CONTACT_CUSTOM_FIELDS]: PLUS,
  [FEATURES.CONTACT_DATABASE_BASIC]: PLUS,
  [FEATURES.CONTACT_NOTES]: PLUS,
  [FEATURES.COPY_TRACKLIST]: PLUS,
  [FEATURES.CUSTOM_FORMAT_SETTINGS]: PLUS,
  [FEATURES.CUSTOM_DOWNLOAD_FILE_NAMES]: PLUS,
  [FEATURES.EMAIL_CAMPAIGN_BUILDER]: PLUS,
  [FEATURES.KILL_URLS]: PLUS,
  [FEATURES.MARKETING_MESSAGE_REMOVED]: PLUS,
  [FEATURES.MESSENGER_EXPRESS]: PLUS,
  [FEATURES.METADATA_ALIAS_BATCH_EDITING]: PLUS,
  [FEATURES.PLAYLIST_STATS_BASIC]: PLUS,
  [FEATURES.PLAYLIST_STATS_WITH_HOVER]: PLUS,
  [FEATURES.PLAYLIST_TAGS]: PLUS,
  [FEATURES.PLAYLIST_THEMES_UNLIMITED]: PLUS,
  [FEATURES.REPORTS_BASIC]: PLUS,
  [FEATURES.SCREENSHARE_TRAINING]: PLUS,
  [FEATURES.TERRITORY_RESTRICTIONS]: PLUS,
  [FEATURES.TRANSCODE_320K]: PLUS,
  [FEATURES.ADMIN_PERMISSIONS_MANAGEMENT_ADVANCED]: PRO,
  [FEATURES.ANALYTICS_UNLIMITED]: PRO,
  [FEATURES.CLIENT_FACING_LIBRARY]: PRO,
  [FEATURES.CONVERT_WAV_AIFF]: PRO,
  [FEATURES.EMAIL_THROUGH_DISCO_BUSINESSES]: PRO,
  [FEATURES.FEATURE_ARTWORK_ON_PLAYLIST]: PRO,
  [FEATURES.INITIAL_IMPORT]: PRO,
  [FEATURES.IN_PERSON_TRAINING]: PRO,
  [FEATURES.MESSENGER_PRIORITY]: PRO,
  [FEATURES.METADATA_ALIAS_INDIVIDUAL_EDITING]: PRO,
  [FEATURES.ONGOING_DDEX]: PRO,
  [FEATURES.PASSWORD_PROTECTION]: PRO,
  [FEATURES.PLAYLIST_SECURITY]: PRO,
  [FEATURES.REPORTS_ADVANCED]: PRO,
  [FEATURES.SHAREABLE_CHANNELS]: PRO,
  [FEATURES.SHARE_EMAIL_TEMPLATE_OPTIONS]: PRO,
  [FEATURES.CUSTOM_INBOXES]: PRO,
  [FEATURES.URL_EXPIRY_DATE]: PRO,
  [FEATURES.COLOR_INDICATORS]: PRO,
  [FEATURES.CUSTOMIZABLE_PLAYLIST_DESIGN]: ENTERPRISE,
  [FEATURES.DEDICATED_ACCOUNT_MANAGER]: ENTERPRISE,
  [FEATURES.DEEP_SECURITY]: ENTERPRISE,
  [FEATURES.DISCO_FOOTER_REMOVED]: ENTERPRISE,
  [FEATURES.PLAYLIST_ORGANIZATION_EXPOSED]: ENTERPRISE,
  [FEATURES.SINGLE_SIGN_ON]: ENTERPRISE,
  [FEATURES.UNLIMITED_TRAINING]: ENTERPRISE,
}
export type featureData = {
  isTrial: boolean
  isFeatureEnabled?: boolean
  isFeatureSuspended?: boolean
  isFeatureUpgradeAvailable?: boolean
  badgeTitle?: string
  // TODO: use separate selector for trialLeft(getTrialLeftSelector) instead of stuffing things in featureData,
  // this left for backwards compatibility and future refactoring
  trialLeft?: TrialLeft | null | undefined
}
/**
 * Gets the current plan for the user's business.
 * @param state - The Redux state
 * @returns The plan object, or null if no plan is available
 */
export const getPlan = (state: State): Plan | null =>
  getUser(state).business?.private_metadata?.plan || null

/**
 * Extracts the feature name from props.
 * @param _ - Unused state parameter
 * @param props - Object containing the feature name
 * @param props.featureName - The feature name to extract
 * @returns The feature name string
 */
const getFeatureName = (
  _: any,
  props: {
    featureName: string
  },
): string => props.featureName

/**
 * Checks if a specific feature is enabled for the user's business.
 * Resolves feature conflicts by removing limited features when unlimited versions are available.
 * @param state - The Redux state
 * @param props - Object containing the feature name
 * @param props.featureName - The feature name to check
 * @returns True if the feature is enabled, false otherwise
 */
export const isFeatureEnabled: (
  state: State,
  arg1: {
    featureName: string
  },
) => boolean = createSelector(
  [getUser, getFeatureName],
  (user, featureName) => {
    const { plan, plans } = user?.business?.private_metadata || {}
    const featuresInPlan = plan?.enabled_features || []
    const featuresInPlans =
      plans?.flatMap(({ enabled_features }) => enabled_features) || []
    // Removing opposite features
    const onlyFeaturesId = {}
    ;[...featuresInPlan, ...featuresInPlans].forEach(({ id }) => {
      onlyFeaturesId[id] = id
    })
    Object.keys(exclusionPairs).forEach(key => {
      const pairKey = exclusionPairs[key]

      if (onlyFeaturesId[key] && onlyFeaturesId[pairKey]) {
        delete onlyFeaturesId[key]
      }
    })
    const enabledFeatures = Object.keys(onlyFeaturesId).map(key => ({
      id: key,
    }))
    return !!enabledFeatures.find(({ id }) => id === featureName)
  },
)
/**
 * Factory function that creates a feature enabled selector.
 * @returns A selector function for checking if features are enabled
 */
export const makeIsFeatureEnabled = (): typeof isFeatureEnabled =>
  isFeatureEnabled
/**
 * Checks if the user is currently on a trial.
 * @param state - The Redux state
 * @returns True if the user is on trial (not a subscriber), false otherwise
 */
export const getIsTrial: (state: State) => boolean = createSelector(
  [state => state],
  state => {
    const subscriber = isFeatureEnabled(state, {
      featureName: FEATURES.IS_SUBSCRIBER,
    })
    return !subscriber
  },
)
/**
 * Gets the badge title for a feature based on trial status and feature type.
 * @param state - The Redux state
 * @param props - Object containing the feature name
 * @param props.featureName - The feature name to get badge title for
 * @returns The badge title string (e.g., 'lite', 'plus', 'pro', 'enterprise', 'upgrade')
 */
const getBadgeTitle: (
  state: State,
  arg1: {
    featureName: string
  },
) => string = createSelector(
  [getIsTrial, getFeatureName],
  (isTrial, featureName) => {
    let title = 'upgrade'

    if (featureName && (isTrial || featureName === FEATURES.COLOR_INDICATORS)) {
      const found = featureLabelMap[featureName]

      if (found) {
        title = found
      }
    }

    return title
  },
)

/**
 * Extracts trial message mode from props.
 * @param _ - Unused state parameter
 * @param props - Optional props object
 * @param props.trialMessageMode - The trial message mode
 * @returns The trial message mode, or null if not provided
 */
const getMessageSettings = (
  _: any,
  props?: {
    trialMessageMode?: TrialMessageMode
  },
): TrialMessageMode | null | undefined => props?.trialMessageMode || null

/**
 * Gets the trial time remaining information.
 * @param state - The Redux state
 * @param props - Optional props object
 * @param props.trialMessageMode - The message mode for trial display
 * @returns Trial time left information, or null if no business/trial
 */
export const getTrialLeftSelector: (
  state: State,
  props?: {
    trialMessageMode?: TrialMessageMode
  },
) => TrialLeft | null | undefined = createSelector(
  [getUser, getMessageSettings],
  (user, trialMessageMode) => {
    const { business } = user
    if (!business) return null
    const { trial_period } = business.private_metadata
    const { trial_started_at, trial_ends_at } = business.stats
    const defaultTrialPeriod = 2592000 // 30 days

    const trialEnd =
      trial_ends_at || trial_started_at + (trial_period || defaultTrialPeriod)
    return getTrialLeft(trialEnd, trialMessageMode || 'short')
  },
)
/**
 * Checks if a feature is specifically suspended (enabled in plan but suspended).
 * @param state - The Redux state
 * @param props - Object containing the feature name
 * @param props.featureName - The feature name to check
 * @returns True if the feature is suspended, false otherwise
 */
const isFeatureSuspended: (
  state: State,
  arg1: {
    featureName: string
  },
) => boolean = createSelector(
  [getUser, getFeatureName],
  (user, featureName) => {
    const suspendedPlanFeatures =
      user?.business?.private_metadata?.suspended_plan_features || []
    return suspendedPlanFeatures.map(f => f.id).includes(featureName)
  },
)
// TODO: remove export
// this function most of the time can be replaced with more "lightwheight" isFeatureEnabled selector
export const getFeatureData: (
  state: State,
  arg1: {
    featureName: string
    trialMessageMode?: TrialMessageMode
  },
) => featureData = createSelector(
  [
    getUser,
    getFeatureName,
    getIsTrial,
    isFeatureEnabled,
    getBadgeTitle,
    getTrialLeftSelector,
    isFeatureSuspended,
  ],
  (
    user,
    featureName,
    isTrial,
    isFeatureEnabled,
    badgeTitle,
    trialLeft,
    isFeatureSuspended,
  ) => {
    const data: featureData = { isTrial }
    const business = user?.business

    if (business) {
      data.trialLeft = trialLeft
    }

    if (featureName) {
      // console.count(`getFeatureData: ${featureName}`)
      data.isFeatureEnabled = isFeatureEnabled
      data.isFeatureSuspended = isFeatureSuspended
      data.isFeatureUpgradeAvailable = !isFeatureEnabled && !isFeatureSuspended
      data.badgeTitle = badgeTitle
    } else {
      // console.count('getFeatureData without featureName')
    }

    return data
  },
)
/**
 * Factory function that creates a feature data selector.
 * @returns A selector function for getting feature data
 */
export const makeGetFeatureData = (): typeof getFeatureData => getFeatureData
// const featureEnabledSelectors = {}
/**
 * React hook that checks if a specific feature is enabled.
 * @param featureName - The name of the feature to check
 * @returns True if the feature is enabled, false otherwise
 */
export function useFeatureEnabled(featureName: string): boolean {
  // if (!featureEnabledSelectors[featureName]) {
  //   featureEnabledSelectors[featureName] = makeIsFeatureEnabled()
  //   console.log(featureName, featureEnabledSelectors)
  //   // if (featureName === 'COPY_METADATA') debugger
  // } else {
  //   // if (featureName === 'COPY_METADATA') debugger
  // }
  // const selector = featureEnabledSelectors[featureName]
  // const selector = makeIsFeatureEnabled()
  const selector = useMemo(makeIsFeatureEnabled, [])
  return useSelector((state: State) =>
    selector(state, {
      featureName,
    }),
  )
}
// const featureDataSelectors = {}
// const nullFetureDataSelector = makeGetFeatureData()
/**
 * React hook that gets feature data including trial status, enabled state, and badge information.
 * @param featureName - The name of the feature to get data for
 * @returns Feature data object containing trial status, enabled state, and other feature information
 */
export function useFeatureData(featureName: string): featureData {
  // if (featureName && !featureDataSelectors[featureName]) {
  //   featureDataSelectors[featureName] = makeGetFeatureData()
  //   console.log(featureName, featureDataSelectors)
  // }
  // const selector = featureName
  //   ? featureDataSelectors[featureName]
  //   : nullFetureDataSelector
  // const selector = makeGetFeatureData()
  const selector = useMemo(makeGetFeatureData, [])
  return useSelector((state: State) =>
    selector(state, {
      featureName,
    }),
  )
}
/**
 * Gets the number of auto-tagging tracks sent by the user's business.
 * @param state - The Redux state
 * @returns Object containing the auto_tagging_tracks_sent count
 */
export const getAutoTaggingTracksSent = (
  state: State,
): {
  auto_tagging_tracks_sent: number
} => {
  const user = state.user
  const { auto_tagging_tracks_sent } = user.business || {}
  return {
    auto_tagging_tracks_sent: auto_tagging_tracks_sent || 0,
  }
}

/**
 * Gets the remaining lyric transcription quota for the user's business.
 * @param state - The Redux state
 * @returns The lyric transcription quota left, or null if not available
 */
export const getLyricTranscriptionQuotaLeft = (state: State) => {
  const user = getUser(state)
  return user.business?.private_metadata?.lyric_transcription_quota_left ?? null
}

/**
 * Gets the business ID for the user's business.
 * @param state - The Redux state
 * @returns The business ID, or null/undefined if not available
 */
export const getBusinessId = (state: State): number | null | undefined =>
  getUser(state).business?.id
/**
 * Gets the rights holders opportunities form URL for the user's business.
 * @param state - The Redux state
 * @returns The opportunities form URL, or null/undefined if not available
 */
export const getRightsHoldersOpportunitiesForm = (
  state: State,
): string | null | undefined => getUser(state).business?.opportunities_form_url
